import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate required environment variables
if (!supabaseUrl) {
  throw new Error('VITE_SUPABASE_URL environment variable is required');
}

if (!supabaseAnonKey) {
  throw new Error('VITE_SUPABASE_ANON_KEY environment variable is required');
}

// Create Supabase client with auth configuration
// IMPORTANT: Both main and prod clients use the same storageKey to share sessions
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    storage: window.localStorage,
    storageKey: 'supabase.auth.token' // Same storage key as prod client
  }
});

// Database inspection utility
/* eslint-disable max-lines-per-function */
export const inspectDatabase = async () => {
  try {
    // Try to query some known tables to see what exists
    const knownTables = [
      'users',
      'user_approvals',
      'user_preferences',
      'vehicles',
      'selected_recalls',
      'dealer_preferences',
      'campaigns',
      'campaign_skill_levels',
      'technicians',
      'technician_work_schedules',
      'technician_assignments',
      'smart_routes',
      'route_confirmations',
      'audit_logs',
      'cool_off_tracking'
    ];

    const existingTables: Array<{ table_name: string }> = [];
    const existingViews: Array<{ table_name: string }> = [];

    // Test each table by trying to select from it
    for (const tableName of knownTables) {
      try {
        const { error } = await supabase.from(tableName).select('*').limit(1);

        if (!error) {
          existingTables.push({ table_name: tableName });
        } else {
          // Table not found or not accessible
        }
      } catch {
        // Ignore per-table errors
      }
    }

    // Test some known views
    const knownViews = ['campaign_technician_view', 'cars_with_safety_recalls'];

    for (const viewName of knownViews) {
      try {
        const { error } = await supabase.from(viewName).select('*').limit(1);

        if (!error) {
          existingViews.push({ table_name: viewName });
        } else {
          // View not found or not accessible
        }
      } catch {
        // Ignore per-view errors
      }
    }

    return { tables: existingTables, views: existingViews, error: null };
  } catch (error) {
    return { tables: null, views: null, error };
  }
};

// Auth helper functions
export const authHelpers = {
  // Sign up with email and password
  async signUp(email: string, password: string) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    return { data, error };
  },

  // Sign in with email and password
  async signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    return { data, error };
  },

  // Sign in with magic link
  async signInWithMagicLink(email: string) {
    const { data, error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    return { data, error };
  },

  // Sign in with OAuth provider
  async signInWithOAuth(provider: 'google' | 'github' | 'discord') {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/auth/callback`
      }
    });
    return { data, error };
  },

  // Sign out
  async signOut() {
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  // Get current session
  async getSession() {
    const {
      data: { session },
      error
    } = await supabase.auth.getSession();
    return { session, error };
  },

  // Get current user
  async getUser() {
    const {
      data: { user },
      error
    } = await supabase.auth.getUser();
    return { user, error };
  },

  // Reset password: OTP flow, user enters code at /auth/reset-password/confirm
  async resetPassword(email: string) {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password/confirm`
    });
    return { data, error };
  },

  // Update password
  async updatePassword(password: string) {
    const { data, error } = await supabase.auth.updateUser({
      password
    });
    return { data, error };
  },

  // Resend confirmation email
  async resendConfirmation(email: string) {
    const { data, error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    return { data, error };
  }
};

// Export the main client for backward compatibility
export { supabase as supabaseClient };

// Re-export tenant-aware client as the new default
export { createTenantAwareSupabaseClient } from './tenant-client';

/**
 * Singleton prod client - created once and reused
 * This client shares the same auth session as the main client via localStorage
 * Use this for querying prod schema tables/views
 */
let prodSupabaseClient: ReturnType<typeof createClient> | null = null;

export const getProdSupabaseClient = () => {
  if (prodSupabaseClient) {
    return prodSupabaseClient;
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  // Validate required environment variables
  if (!supabaseUrl) {
    throw new Error('VITE_SUPABASE_URL environment variable is required for prod client');
  }

  if (!supabaseAnonKey) {
    throw new Error('VITE_SUPABASE_ANON_KEY environment variable is required for prod client');
  }

  prodSupabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
    db: { schema: 'prod' },
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
      storage: window.localStorage,
      storageKey: 'supabase.auth.token' // Same storage key as main client
    }
  });

  return prodSupabaseClient;
};

/**
 * Hydrate sessions for both main and prod clients
 * This forces both clients to read localStorage and cache session/token in memory
 * Call this once at app initialization before any queries
 * Only hydrates if a session exists - doesn't attempt to sign in
 */
export async function hydrateSessions() {
  try {
    // Forces both clients to read localStorage and cache session/token in memory
    // getSession() just reads from storage - it doesn't attempt to sign in
    await Promise.all([supabase.auth.getSession(), getProdSupabaseClient().auth.getSession()]);
  } catch {
    // Silently fail - session might not exist yet (user not logged in)
    // This is fine - clients will hydrate when user logs in
  }
}
